import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';
import { useStoreActions } from '~/store/hooks';
import Loader from '~/ui/components/common/Loader';
import { NETWORK } from '~/ui/constants/paths';
import { NETWORK_TAB_SLUGS } from '~/ui/pages/Network/constants';

import useFormStorage from '../hooks/useFormStorage';
import normalizeAllocationsByUser from '../shared/model/normalizeAllocationsByUser';
import AddAllocationFormComponent from './components/AllocationForm/AllocationForm';
import { useAddClientAllocationContext } from './context/AddClientAllocationContext';
import useAddAllocationSubmit from './hooks/submitAllocations/useAddAllocationSubmit';
import { IAddAllocationFormValues } from './types';

const AddClientAllocationForm = (): ReactElement => {
  const { teamMembers, isLoading } = useAddClientAllocationContext();
  const navigate = useNavigate();
  const { showNotify } = useStoreActions(actions => actions.snackbar);
  const { resetClientAllocationAvailability } = useStoreActions(
    actions => actions.clientAllocation,
  );

  const defaultValues = useMemo<IAddAllocationFormValues>(
    () => ({
      dateRange: [],
      includeWeekends: false,
      teamMembersAllocations: normalizeAllocationsByUser([], teamMembers),
    }),
    [teamMembers],
  );

  const form = useForm<IAddAllocationFormValues>({
    values: defaultValues,
  });
  const { handleSubmit } = form;

  const { isRestored, clearDraft } = useFormStorage(
    form,
    { key: DRAFT_ALLOCATION_KEY },
    defaultValues,
  );

  useEffect(() => {
    resetClientAllocationAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitSuccess = useCallback(() => {
    clearDraft();
    navigate(`${NETWORK}?tab=${NETWORK_TAB_SLUGS.MANAGE_ALLOCATION}`);
  }, [clearDraft, navigate]);

  const { submit, isSubmitting } = useAddAllocationSubmit();

  const combinedSubmit = useCallback(
    async (values: IAddAllocationFormValues) => {
      const success = await submit(values);

      if (success) {
        showNotify({ message: 'Client allocation successfully created' });
        handleSubmitSuccess();
      }
    },
    [handleSubmitSuccess, showNotify, submit],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(combinedSubmit)}>
        <AddAllocationFormComponent
          isRestored={isRestored}
          isSubmitting={isSubmitting}
          clearDraft={clearDraft}
        />
      </form>
    </FormProvider>
  );
};

export default AddClientAllocationForm;
